import { useState, useEffect, memo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  PlusCircleIcon,
  XIcon,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";

// Import Cloudinary upload functionality
import { useCloudinaryUpload } from "@/hooks/use-cloudinary-upload";
import { SupplierInfo, ProductInfo } from "@/lib/cloudinary-upload-service";

// Add the missing cn utility function
function cn(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

// Define the form schema with validation rules
const productFormSchema = z.object({
  name: z
    .string()
    .min(3, { message: "Product name must be at least 3 characters" }),
  categoryId: z.string().min(1, { message: "Please select a category" }),
  subCategoryId: z.string().min(1, { message: "Please select a subcategory" }),
  productCategoryId: z
    .string()
    .min(1, { message: "Please select a product category" }),
  brand: z.string().optional(),
  model: z.string().optional(),
  condition: z.enum(["New", "Used", "Refurbished"]),
  price: z.coerce
    .number()
    .positive({ message: "Price must be a positive number" }),
  description: z.string().optional(),
  specifications: z.array(
    z.object({
      name: z.string().min(1, { message: "Specification name is required" }),
      value: z.string().min(1, { message: "Specification value is required" }),
    })
  ),
  images: z.array(z.any()).optional(),
  videoUrl: z.string().optional(),
  videoFile: z.any().optional(),
  certifications: z.array(z.any()).optional(),
  location: z.string().optional(),
  shippingOptions: z.object({
    localPickup: z.boolean().default(false),
    domesticShipping: z.boolean().default(false),
    internationalShipping: z.boolean().default(false),
  }),
  year: z.coerce
    .number()
    .min(1900)
    .max(new Date().getFullYear() + 1)
    .optional(),
  hoursUsed: z.coerce.number().min(0).optional(),
  featured: z.boolean().default(false),
  inStock: z.boolean().default(true),
  quantity: z.coerce.number().min(1).default(1),
  sku: z.string().optional(),
});

// Define the type for our form values
type ProductFormValues = z.infer<typeof productFormSchema>;

// Define types for database entities
type Category = {
  id: string;
  name: string;
  image_url?: string;
  created_at?: string;
};

type Subcategory = {
  id: string;
  name: string;
  category_id: string;
  created_at?: string;
};

type ProductCategory = {
  id: string;
  name: string;
  subcategory_id: string;
  created_at?: string;
};

// Define types for image and certification uploads
type UploadedFile = {
  file: File;
  preview: string;
};

// Props interface for the ProductAddForm component
interface ProductAddFormProps {
  isEditing?: boolean;
  existingProduct?: any; // We'll use any for now to avoid complex type definitions
  onEditSuccess?: () => void;
}

export default memo(function ProductAddForm({ 
  isEditing = false, 
  existingProduct = null, 
  onEditSuccess 
}: ProductAddFormProps) {
  console.log('🔄 ProductAddForm render/creation', { isEditing, hasExistingProduct: !!existingProduct });
  
  // Navigation hook
  const navigate = useNavigate();
  
  // State for categories, subcategories, and product categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // State for available options based on selections
  const [availableSubCategories, setAvailableSubCategories] = useState<Subcategory[]>([]);
  const [availableProductCategories, setAvailableProductCategories] = useState<ProductCategory[]>([]);
  
  // State for uploaded files
  const [uploadedImages, setUploadedImages] = useState<UploadedFile[]>([]);
  const [uploadedCertifications, setUploadedCertifications] = useState<UploadedFile[]>([]);
  
  // State for form submission feedback
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cloudinary upload hook
  const {
    uploadMultipleImages,
    isUploading: isUploadingToCloudinary,
    uploadProgress,
    uploadedCount,
    totalCount,
    currentFile,
    error: uploadError,
    results: uploadResults,
    configValid,
    configErrors,
    resetState: resetUploadState
  } = useCloudinaryUpload({
    onProgress: (progress, completed, total, currentFile) => {
      console.log(`📤 Cloudinary upload progress: ${progress}% (${completed}/${total}) - Current: ${currentFile || 'N/A'}`);
    },
    onSuccess: (results) => {
      console.log('✅ All images uploaded to Cloudinary successfully:', results);
    },
    onError: (error) => {
      console.error('❌ Cloudinary upload error:', error);
    },
    onFileValidationError: (file, error) => {
      console.warn(`⚠️ File validation failed for ${file.name}: ${error}`);
    },
    uploadOptions: {
      maxRetries: 3,
      retryDelay: 1000
    }
  });

  // Initialize the form with react-hook-form and zod validation
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema) as any,
    defaultValues: isEditing && existingProduct ? {
      name: existingProduct.name || "",
      categoryId: existingProduct.category_id || "",
      subCategoryId: existingProduct.sub_category_id || "",
      productCategoryId: existingProduct.product_category_id || "",
      brand: existingProduct.brand || "",
      model: existingProduct.model || "",
      condition: existingProduct.condition || "New",
      price: existingProduct.price || 0,
      description: existingProduct.description || "",
      specifications: existingProduct.specifications?.map((spec: any) => ({
        name: spec.specification_name || spec.name || "",
        value: spec.specification_value || spec.value || ""
      })) || [],
      images: existingProduct.images || [],
      videoUrl: existingProduct.video_url || "",
      videoFile: undefined,
      certifications: existingProduct.certifications || [],
      location: existingProduct.location || "",
      shippingOptions: existingProduct.shipping_options || {
        localPickup: true,
        domesticShipping: false,
        internationalShipping: false,
      },
      year: existingProduct.year || new Date().getFullYear(),
      hoursUsed: existingProduct.hours_used || undefined,
      featured: existingProduct.featured || false,
      inStock: existingProduct.in_stock ?? true,
      quantity: existingProduct.quantity || 1,
      sku: existingProduct.sku || "",
    } : {
      name: "",
      categoryId: "",
      subCategoryId: "",
      productCategoryId: "",
      brand: "",
      model: "",
      condition: "New",
      price: 0,
      description: "",
      specifications: [],
      images: [],
      videoUrl: "",
      videoFile: undefined,
      certifications: [],
      location: "",
      shippingOptions: {
        localPickup: true,
        domesticShipping: false,
        internationalShipping: false,
      },
      year: new Date().getFullYear(),
      hoursUsed: undefined,
      featured: false,
      inStock: true,
      quantity: 1,
      sku: "",
    },
  });

  // Get the form state
  const { control, setValue, watch, reset } = form;

  // Use useFieldArray for dynamic specification fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "specifications",
  });
  
  // Watch for category and subcategory changes
  const watchCategoryId = watch("categoryId");
  const watchSubCategoryId = watch("subCategoryId");

  // Load saved form data from localStorage on component mount (skip in editing mode)
  useEffect(() => {
    if (isEditing) {
      console.log('Skipping localStorage loading in editing mode');
      return;
    }
    
    try {
      const savedData = localStorage.getItem('productFormData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        console.log('📥 Loading saved form data from localStorage:', parsedData);
        
        // Merge saved data with default values to ensure all required fields exist
        const defaultValues = {
          name: "",
          categoryId: "",
          subCategoryId: "",
          productCategoryId: "",
          brand: "",
          model: "",
          condition: "New" as const,
          price: 0,
          description: "",
          specifications: [],
          images: [],
          videoUrl: "",
          videoFile: undefined,
          certifications: [],
          location: "",
          shippingOptions: {
            localPickup: true,
            domesticShipping: false,
            internationalShipping: false,
          },
          year: new Date().getFullYear(),
          hoursUsed: undefined,
          featured: false,
          inStock: true,
          quantity: 1,
          sku: "",
        };
        
        // Merge saved data with defaults, ensuring all fields are present
        const mergedData = {
          ...defaultValues,
          ...parsedData,
          shippingOptions: {
            ...defaultValues.shippingOptions,
            ...(parsedData.shippingOptions || {})
          }
        };
        
        console.log('🔄 Merged form data:', mergedData);
        
        // Reset form with merged data
        reset(mergedData);
        
        // Also restore uploaded files state if available
        const savedImages = localStorage.getItem('productFormImages');
        const savedCertifications = localStorage.getItem('productFormCertifications');
        
        // Note: We don't restore image/certification files from localStorage
        // because File objects cannot be serialized/deserialized properly.
        // Users will need to re-upload files if they refresh the page.
        // This prevents errors during form submission.
        
        if (savedImages) {
          console.log('ℹ️ Found saved image metadata, but files need to be re-uploaded');
          // Don't restore images - they need to be re-uploaded
          // const parsedImages = JSON.parse(savedImages);
          // setUploadedImages(parsedImages);
        }
        
        if (savedCertifications) {
          console.log('ℹ️ Found saved certification metadata, but files need to be re-uploaded');
          // Don't restore certifications - they need to be re-uploaded
          // const parsedCertifications = JSON.parse(savedCertifications);
          // setUploadedCertifications(parsedCertifications);
        }
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
      // Clear corrupted data
      localStorage.removeItem('productFormData');
      localStorage.removeItem('productFormImages');
      localStorage.removeItem('productFormCertifications');
    }
  }, [reset]);

  // Save form data to localStorage whenever form values change (skip in editing mode)
  useEffect(() => {
    if (isEditing) {
      return; // Don't save to localStorage when editing
    }
    
    const subscription = watch((value) => {
      try {
        // Only save if there's meaningful data (not just empty form)
        const hasData = value.name || value.description || (value.price && value.price > 0) || 
                       value.brand || value.model || value.categoryId;
        
        if (hasData) {
          console.log('💾 Saving form data to localStorage');
          localStorage.setItem('productFormData', JSON.stringify(value));
          localStorage.setItem('productFormLastSaved', new Date().toISOString());
        }
      } catch (error) {
        console.error('Error saving form data:', error);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch]);

  // Save uploaded files to localStorage (as metadata, not actual files)
  useEffect(() => {
    try {
      if (uploadedImages.length > 0) {
        const imageMetadata = uploadedImages.map(img => ({
          name: img.file.name,
          size: img.file.size,
          type: img.file.type,
          preview: img.preview
        }));
        localStorage.setItem('productFormImages', JSON.stringify(imageMetadata));
      }
    } catch (error) {
      console.error('Error saving image metadata:', error);
    }
  }, [uploadedImages]);

  useEffect(() => {
    try {
      if (uploadedCertifications.length > 0) {
        const certMetadata = uploadedCertifications.map(cert => ({
          name: cert.file.name,
          size: cert.file.size,
          type: cert.file.type,
          preview: cert.preview
        }));
        localStorage.setItem('productFormCertifications', JSON.stringify(certMetadata));
      }
    } catch (error) {
      console.error('Error saving certification metadata:', error);
    }
  }, [uploadedCertifications]);

  // Clear saved data when form is successfully submitted
  const clearSavedData = () => {
    localStorage.removeItem('productFormData');
    localStorage.removeItem('productFormImages');
    localStorage.removeItem('productFormCertifications');
    console.log('🗑️ Cleared saved form data from localStorage');
  };

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .order("name");

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (watchCategoryId) {
      const fetchSubcategories = async () => {
        try {
          const { data, error } = await supabase
            .from("subcategories")
            .select("*")
            .eq("category_id", watchCategoryId)
            .order("name");

          if (error) throw error;
          setAvailableSubCategories(data || []);
        } catch (error) {
          console.error("Error fetching subcategories:", error);
        }
      };
      
      fetchSubcategories();
    } else {
      setAvailableSubCategories([]);
    }
  }, [watchCategoryId]);

  // Fetch product categories when subcategory changes
  useEffect(() => {
    if (watchSubCategoryId) {
      const fetchProductCategories = async () => {
        try {
          const { data, error } = await supabase
            .from("product_categories")
            .select("*")
            .eq("subcategory_id", watchSubCategoryId)
            .order("name");

          if (error) throw error;
          setAvailableProductCategories(data || []);
        } catch (error) {
          console.error("Error fetching product categories:", error);
        }
      };
      
      fetchProductCategories();
    } else {
      setAvailableProductCategories([]);
    }
  }, [watchSubCategoryId]);

  // Initialize subcategories and product categories for edit mode
  useEffect(() => {
          if (isEditing && existingProduct && categories.length > 0) {
        console.log('🔄 Initializing dependent categories for edit mode');
        console.log('📋 Existing product data:', {
          category_id: existingProduct.category_id,
          sub_category_id: existingProduct.sub_category_id,
          product_category_id: existingProduct.product_category_id
        });
      console.log('📋 Existing product data:', {
        category_id: existingProduct.category_id,
        sub_category_id: existingProduct.sub_category_id,
        product_category_id: existingProduct.product_category_id
      });
      
      // Fetch subcategories if we have a category_id
      if (existingProduct.category_id) {
        const fetchSubcategories = async () => {
          try {
            console.log('🔍 Fetching subcategories for category:', existingProduct.category_id);
            const { data, error } = await supabase
              .from("subcategories")
              .select("*")
              .eq("category_id", existingProduct.category_id)
              .order("name");

            if (error) throw error;
            console.log('📋 Loaded subcategories for edit mode:', data?.length || 0, data);
            setAvailableSubCategories(data || []);
            
            // After subcategories are loaded, fetch product categories if we have sub_category_id
            if (existingProduct.sub_category_id) {
              console.log('🔍 Fetching product categories for subcategory:', existingProduct.sub_category_id);
              const { data: productCategoriesData, error: productCategoriesError } = await supabase
                .from("product_categories")
                .select("*")
                .eq("subcategory_id", existingProduct.sub_category_id)
                .order("name");

              if (productCategoriesError) throw productCategoriesError;
              console.log('📋 Loaded product categories for edit mode:', productCategoriesData?.length || 0, productCategoriesData);
              setAvailableProductCategories(productCategoriesData || []);
            }
          } catch (error) {
            console.error("Error fetching dependent categories for edit mode:", error);
          }
        };
        
        fetchSubcategories();
      }
    }
  }, [isEditing, existingProduct, categories.length]);

  // Initialize existing images in edit mode
  useEffect(() => {
    if (isEditing && existingProduct && existingProduct.images && existingProduct.images.length > 0) {
      console.log('🖼️ Initializing existing images for edit mode:', existingProduct.images);
      
      const existingImages: UploadedFile[] = existingProduct.images.map((imageUrl: string, index: number) => ({
        file: new File([], `existing-image-${index}`, { type: 'image/jpeg' }), // Dummy file object
        preview: imageUrl // Use the actual URL as preview
      }));
      
      setUploadedImages(existingImages);
    }
  }, [isEditing, existingProduct]);

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const newFiles: UploadedFile[] = [];
    const filesArray = Array.from(e.target.files);

    filesArray.forEach((file) => {
      const preview = URL.createObjectURL(file);
      newFiles.push({ file, preview });
    });

    setUploadedImages([...uploadedImages, ...newFiles]);
    setValue("images", [...(watch("images") || []), ...filesArray]);
  };

  // Handle certification upload
  const handleCertificationUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const newFiles: UploadedFile[] = [];
    const filesArray = Array.from(e.target.files);

    filesArray.forEach((file) => {
      const preview = URL.createObjectURL(file);
      newFiles.push({ file, preview });
    });

    setUploadedCertifications([...uploadedCertifications, ...newFiles]);
    setValue("certifications", [...(watch("certifications") || []), ...filesArray]);
  };

  // Remove an image
  const removeImage = (index: number) => {
    const imageToRemove = uploadedImages[index];
    
    // If this is a newly uploaded file, revoke the object URL
    if (!isEditing || !imageToRemove.preview.startsWith('http')) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    
    console.log('🗑️ Removing image:', imageToRemove.preview);
    
    const newImages = [...uploadedImages];
    newImages.splice(index, 1);
    setUploadedImages(newImages);
    
    const newImagesArray = [...(watch("images") || [])];
    newImagesArray.splice(index, 1);
    setValue("images", newImagesArray);
  };

  // Remove a certification
  const removeCertification = (index: number) => {
    const newCertifications = [...uploadedCertifications];
    URL.revokeObjectURL(newCertifications[index].preview);
    newCertifications.splice(index, 1);
    setUploadedCertifications(newCertifications);
    const newCertificationsArray = [...(watch("certifications") || [])];
    newCertificationsArray.splice(index, 1);
    setValue("certifications", newCertificationsArray);
  };

  // Loading indicator for initial data fetch
  if (loading && categories.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  // Handle form submission
  const onSubmit = async (data: ProductFormValues) => {
    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      console.log('⏳ Submission already in progress, ignoring...');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setLoading(true);
      setError(null);
      
      console.log('🚀 Starting form submission...');
      
      // Debug: Log the form data to see what we're receiving
      console.log('📝 Form submission data:', data);
      console.log('📝 Form state:', form.formState);
      console.log('📝 Form errors:', form.formState.errors);
      
      // Check if data is undefined or null
      if (!data) {
        console.error('❌ Form data is undefined or null');
        console.log('🔄 Attempting to get form values manually...');
        
        // Try to get form values manually as a fallback
        const formValues = form.getValues();
        console.log('📝 Manual form values:', formValues);
        
        if (formValues && Object.keys(formValues).length > 0) {
          data = formValues as ProductFormValues;
          console.log('✅ Using manual form values');
        } else {
          throw new Error('Form data is missing. Please try again.');
        }
      }
      
      // Create a deep copy of the data to prevent it from being modified during async operations
      const submissionData = JSON.parse(JSON.stringify(data)) as ProductFormValues;
      console.log('📋 Submission data copy:', submissionData);
      
      // Separate existing images (URLs) from new uploads (Files) in edit mode
      const existingImages: string[] = [];
      const newUploads: UploadedFile[] = [];
      
      uploadedImages.forEach(image => {
        if (isEditing && image.preview.startsWith('http')) {
          // This is an existing image URL
          existingImages.push(image.preview);
        } else if (image && image.file && typeof image.file === 'object' && 
                   image.file.name && typeof image.file.name === 'string') {
          // This is a new upload
          newUploads.push(image);
        } else {
          console.warn('⚠️ Filtering out invalid image object:', image);
        }
      });
      
      console.log('📸 Image separation:', {
        existingImages: existingImages.length,
        newUploads: newUploads.length,
        existingUrls: existingImages,
      });
      
      // For backward compatibility, keep validImages as newUploads
      const validImages = newUploads;
      
      if (validImages.length !== uploadedImages.length) {
        console.log(`🔧 Filtered images: ${uploadedImages.length} -> ${validImages.length}`);
        // Update the state to remove invalid images
        setUploadedImages(validImages);
      }
      
      // Validate the data against our schema
      try {
        const validatedData = productFormSchema.parse(submissionData);
        console.log('✅ Form validation passed:', validatedData);
        // Use the validated data for the rest of the function
        Object.assign(submissionData, validatedData);
      } catch (validationError) {
        console.error('❌ Form validation failed:', validationError);
        throw new Error('Form validation failed. Please check all required fields.');
      }
      
      // Validate required fields with additional safety checks
      if (!submissionData.name || typeof submissionData.name !== 'string' || submissionData.name.trim() === '') {
        throw new Error('Product name is required');
      }
      
      if (!submissionData.categoryId || typeof submissionData.categoryId !== 'string') {
        throw new Error('Category is required');
      }
      
      if (!submissionData.subCategoryId || typeof submissionData.subCategoryId !== 'string') {
        throw new Error('Subcategory is required');
      }
      
      if (!submissionData.productCategoryId || typeof submissionData.productCategoryId !== 'string') {
        throw new Error('Product category is required');
      }
      
      if (!submissionData.price || typeof submissionData.price !== 'number' || submissionData.price <= 0) {
        throw new Error('Valid price is required');
      }
      
      // Get the current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!userData.user) throw new Error('User not authenticated');
      
      // Generate a slug from the product name
      const slug = submissionData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .concat('-', Math.floor(Math.random() * 1000).toString());
      
      console.log('🔗 Generated slug:', slug);
      
      // Upload product images if any
      const imageUrls = [];
      if (validImages.length > 0) {
        console.log('📸 Processing uploaded images with Cloudinary:', validImages);
        
        // Get supplier information for folder organization
        const supplierInfo: SupplierInfo = {
          id: userData.user.id,
          name: userData.user.user_metadata?.full_name || userData.user.email || 'Unknown Supplier',
          email: userData.user.email
        };
        
        // Create product info for folder organization
        const productInfo: ProductInfo = {
          name: submissionData.name,
          slug: slug
        };
        
        try {
          // Upload images to Cloudinary with supplier folder organization
          const files = validImages.map(img => img.file);
          const cloudinaryResults = await uploadMultipleImages(files, supplierInfo, productInfo);
          
          // Extract successful upload URLs
          const successfulUploads = cloudinaryResults.filter(result => result.uploadStatus === 'Success');
          imageUrls.push(...successfulUploads.map(result => result.imageUrl));
          
          console.log(`✅ Successfully uploaded ${successfulUploads.length}/${cloudinaryResults.length} images to Cloudinary`);
          
          // Log failed uploads
          const failedUploads = cloudinaryResults.filter(result => result.uploadStatus !== 'Success');
          if (failedUploads.length > 0) {
            console.warn('⚠️ Some image uploads failed:', failedUploads);
          }
          
        } catch (imageError) {
          console.error('❌ Error uploading images to Cloudinary:', imageError);
          throw new Error(`Image upload failed: ${imageError instanceof Error ? imageError.message : 'Unknown error'}`);
        }
        
        console.log('📸 Total images processed with Cloudinary:', imageUrls.length);
      }
      
      // Create or update the product in the database
      let productData, productError;
      
      if (isEditing && existingProduct) {
        // Update existing product
        console.log('🔄 Updating existing product:', existingProduct.id);
        
        const updateData = {
          name: submissionData.name,
          category_id: submissionData.categoryId,
          subcategory_id: submissionData.subCategoryId,
          product_category_id: submissionData.productCategoryId,
          condition: submissionData.condition,
          price: submissionData.price,
          description: submissionData.description || null,
          features: submissionData.specifications && submissionData.specifications.length > 0 
            ? JSON.stringify(submissionData.specifications) 
            : null,
          year: submissionData.year || null,
          manufacturer: submissionData.brand || null,
          model: submissionData.model || null,
          hours_used: submissionData.hoursUsed || null,
          location: submissionData.location || null,
          featured: submissionData.featured || false,
          in_stock: submissionData.inStock !== undefined ? submissionData.inStock : true,
          delivery_available: submissionData.shippingOptions?.domesticShipping || 
                             submissionData.shippingOptions?.internationalShipping || 
                             false,
          updated_at: new Date().toISOString(),
        };
        
        ({ data: productData, error: productError } = await supabase
          .from('products')
          .update(updateData)
          .eq('id', existingProduct.id)
          .eq('supplier_id', userData.user.id) // Security check
          .select()
          .single());
      } else {
        // Create new product
        console.log('✨ Creating new product');
        
        ({ data: productData, error: productError } = await supabase
          .from('products')
          .insert({
            name: submissionData.name,
            slug: slug,
            category_id: submissionData.categoryId,
            subcategory_id: submissionData.subCategoryId,
            product_category_id: submissionData.productCategoryId,
            supplier_id: userData.user.id,
            condition: submissionData.condition,
            price: submissionData.price,
            currency: 'INR', // Default currency from schema
            description: submissionData.description || null,
            features: submissionData.specifications && submissionData.specifications.length > 0 
              ? JSON.stringify(submissionData.specifications) 
              : null,
            year: submissionData.year || null,
            manufacturer: submissionData.brand || null,
            model: submissionData.model || null,
            hours_used: submissionData.hoursUsed || null,
            location: submissionData.location || null,
            featured: submissionData.featured || false,
            in_stock: submissionData.inStock !== undefined ? submissionData.inStock : true,
            delivery_available: submissionData.shippingOptions?.domesticShipping || 
                               submissionData.shippingOptions?.internationalShipping || 
                               false,
            // Set defaults for fields not in form but required by schema
            rating: 0,
            review_count: 0,
            views_count: 0,
            financing_available: false,
            warranty: '',
            // brand_id: null, // We'll handle brand relationships later
          })
          .select()
          .single());
      }
        
      console.log('📊 Product insert result:', { productData, productError });
        
      if (productError) {
        console.error('❌ Product insert error:', productError);
        throw new Error(`Database error: ${productError.message}`);
      }
      
      // Handle image updates in edit mode
      if (isEditing && existingProduct) {
        // Clear all existing images and re-insert them with correct order
        console.log('🗑️ Clearing all existing images for re-ordering');
        await supabase
          .from('product_images')
          .delete()
          .eq('product_id', existingProduct.id);
      }

      // Handle new image uploads and preserve existing images
      const allImageUrls: string[] = [];
      
      // Add existing images that weren't deleted
      if (isEditing && existingImages.length > 0) {
        allImageUrls.push(...existingImages);
      }
      
      // Add newly uploaded images
      if (imageUrls.length > 0) {
        allImageUrls.push(...imageUrls);
      }
      
      if (allImageUrls.length > 0) {
        console.log('📸 Processing all product images:', allImageUrls);
        
        const imageInserts = allImageUrls.map((url, index) => ({
          product_id: productData.id,
          image_url: url,
          is_primary: index === 0, // First image is primary
          display_order: index,
        }));
        
        console.log('📸 Image insert data:', imageInserts);
        
        const { error: imageInsertError } = await supabase
          .from('product_images')
          .insert(imageInserts);
          
        if (imageInsertError) {
          console.error('❌ Image insert error:', imageInsertError);
          throw new Error(`Image upload error: ${imageInsertError.message}`);
        }
        
        console.log('✅ Images processed successfully');
      }
      
      // Handle success based on mode
      if (isEditing) {
        console.log('✅ Product updated successfully');
        // Call the edit success callback if provided
        if (onEditSuccess) {
          onEditSuccess();
        } else {
          // Fallback navigation
          navigate('/supplier-dashboard?tab=products&success=true');
        }
      } else {
        // Reset form and show success message for new product creation
        form.reset();
        setUploadedImages([]);
        setUploadedCertifications([]);
        setSuccess(true);
        
        // Clear saved data from localStorage since form was successfully submitted
        clearSavedData();
        
        // Redirect to supplier dashboard after short delay
        setTimeout(() => {
          navigate('/supplier-dashboard?tab=products&success=true');
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error adding product:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to add product. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('validation')) {
          errorMessage = 'Please fill in all required fields correctly.';
        } else if (error.message.includes('name')) {
          errorMessage = 'Product name is required and must be at least 3 characters.';
        } else if (error.message.includes('category')) {
          errorMessage = 'Please select a valid category, subcategory, and product category.';
        } else if (error.message.includes('price')) {
          errorMessage = 'Please enter a valid price greater than 0.';
        } else if (error.message.includes('auth')) {
          errorMessage = 'You must be logged in to add products.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      console.log('🏁 Form submission completed');
    }
  }; 
  
  // Custom form submission handler as a fallback
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      console.log('⏳ Custom submission already in progress, ignoring...');
      return;
    }
    
    console.log('🚀 Custom form submission triggered');
    
    try {
      // Manually trigger validation
      const isValid = await form.trigger();
      console.log('🔍 Manual validation result:', isValid);
      
      if (!isValid) {
        console.error('❌ Form validation failed');
        const errors = form.formState.errors;
        console.log('📋 Validation errors:', errors);
        setError('Please fill in all required fields correctly.');
        return;
      }
      
      // Get form values manually
      const formValues = form.getValues();
      console.log('📝 Manual form values for submission:', formValues);
      
      // Call our onSubmit function with the form values
      await onSubmit(formValues);
    } catch (error) {
      console.error('❌ Custom form submission error:', error);
      setError('Form submission failed. Please try again.');
    }
  };
  
  // Test function to validate form data structure
  const testFormData = () => {
    const formValues = form.getValues();
    console.log('🧪 Testing form data structure:');
    console.log('📋 Raw form values:', formValues);
    
    // Test required fields
    const requiredFields: (keyof ProductFormValues)[] = ['name', 'categoryId', 'subCategoryId', 'productCategoryId', 'price'];
    const missingFields = requiredFields.filter(field => !formValues[field]);
    
    if (missingFields.length > 0) {
      console.error('❌ Missing required fields:', missingFields);
      return false;
    }
    
    // Test data types
    const typeTests = {
      name: typeof formValues.name === 'string',
      categoryId: typeof formValues.categoryId === 'string',
      subCategoryId: typeof formValues.subCategoryId === 'string', 
      productCategoryId: typeof formValues.productCategoryId === 'string',
      price: typeof formValues.price === 'number' && formValues.price > 0,
      condition: ['New', 'Used', 'Refurbished'].includes(formValues.condition),
    };
    
    const failedTypes = Object.entries(typeTests)
      .filter(([_, passed]) => !passed)
      .map(([field]) => field);
    
    if (failedTypes.length > 0) {
      console.error('❌ Type validation failed for fields:', failedTypes);
      return false;
    }
    
    console.log('✅ All form data validation passed');
    return true;
  };
  
  return (
    <Form {...form}>
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Product {isEditing ? 'updated' : 'added'} successfully! {isEditing ? 'Redirecting...' : 'Redirecting...'}</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Cloudinary Configuration Status */}
      {!configValid && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded mb-6 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <strong>⚠️ Cloudinary Configuration Issues:</strong>
            <ul className="mt-1 ml-4 list-disc">
              {configErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
            <p className="mt-2 text-sm">Please check your .env file and ensure all Cloudinary variables are set correctly.</p>
          </div>
        </div>
      )}
      
      <form onSubmit={handleFormSubmit} className="space-y-8">

        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
              <CardDescription>Enter the basic details of your product</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter product name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subcategory*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!watchCategoryId || availableSubCategories.length === 0}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a subcategory" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableSubCategories.map((subcategory) => (
                            <SelectItem key={subcategory.id} value={subcategory.id}>
                              {subcategory.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="productCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Category*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!watchSubCategoryId || availableProductCategories.length === 0}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a product category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableProductCategories.map((productCategory) => (
                            <SelectItem key={productCategory.id} value={productCategory.id}>
                              {productCategory.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Brand</FormLabel>
                        <FormControl>
                          <Input placeholder="Brand name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="model"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Model</FormLabel>
                        <FormControl>
                          <Input placeholder="Model number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="condition"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Condition*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Used">Used</SelectItem>
                          <SelectItem value="Refurbished">Refurbished</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price*</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe your product in detail"
                          className="min-h-32"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Image Upload Section */}
                <div className="space-y-2">
                  <FormLabel>Product Images</FormLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative border rounded-md p-1">
                        <img
                          src={image.preview}
                          alt={`Product image ${index + 1}`}
                          className="w-full h-32 object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6"
                          onClick={() => removeImage(index)}
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="border border-dashed rounded-md flex items-center justify-center h-32">
                      <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center p-4">
                        <div className="rounded-full bg-muted p-2 mb-2">
                          <PlusCircleIcon className="h-4 w-4" />
                        </div>
                        <span className="text-sm text-muted-foreground">Add Image</span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </label>
                    </div>
                  </div>
                  <FormDescription>
                    Upload high-quality images of your product. Maximum 5 images.
                  </FormDescription>
                </div>
                
                {/* Certification Upload Section */}
                <div className="space-y-2">
                  <FormLabel>Certifications & Documents</FormLabel>
                  <div className="space-y-2">
                    {uploadedCertifications.map((cert, index) => (
                      <div key={index} className="flex items-center justify-between border rounded-md p-2">
                        <div className="flex items-center gap-2">
                          <div className="bg-muted p-2 rounded">
                            <PlusCircleIcon className="h-4 w-4" />
                          </div>
                          <span className="text-sm truncate max-w-[200px]">
                            {cert.file.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCertification(index)}
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="border border-dashed rounded-md p-4">
                      <label htmlFor="certification-upload" className="cursor-pointer flex flex-col items-center">
                        <div className="rounded-full bg-muted p-2 mb-2">
                          <PlusCircleIcon className="h-4 w-4" />
                        </div>
                        <span className="text-sm text-muted-foreground">Add Certification or Document</span>
                        <input
                          id="certification-upload"
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          multiple
                          className="hidden"
                          onChange={handleCertificationUpload}
                        />
                      </label>
                    </div>
                  </div>
                  <FormDescription>
                    Upload relevant certifications, compliance documents, or product manuals.
                  </FormDescription>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
              <CardDescription>Provide more information about your product</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <FormLabel>Specifications</FormLabel>
                  <div className="mt-2 space-y-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className="flex items-center space-x-2">
                        <FormField
                          control={form.control}
                          name={`specifications.${index}.name`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input placeholder="Name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`specifications.${index}.value`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input placeholder="Value" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => append({ name: "", value: "" })}
                    >
                      <PlusCircleIcon className="mr-2 h-4 w-4" />
                      Add Specification
                    </Button>
                  </div>
                </div>
                
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input placeholder="Product location" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel>Shipping Options</FormLabel>
                  <div className="mt-2 space-y-2">
                    <FormField
                      control={form.control}
                      name="shippingOptions.localPickup"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            Local Pickup
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="shippingOptions.domesticShipping"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            Domestic Shipping
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="shippingOptions.internationalShipping"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            International Shipping
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Form Actions */}
        <div className="space-y-4">
          {/* Auto-save indicator */}
          <div className="text-sm text-muted-foreground text-center">
            {(() => {
              try {
                const lastSaved = localStorage.getItem('productFormLastSaved');
                if (lastSaved) {
                  const date = new Date(lastSaved);
                  return `💾 Auto-saved at ${date.toLocaleTimeString()}`;
                }
              } catch (error) {
                console.error('Error getting last saved time:', error);
              }
              return 'Form data will be auto-saved as you type';
            })()}
          </div>
          
          {/* File upload notice */}
          <div className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded p-2">
            📁 Note: Images are uploaded to Cloudinary and organized by supplier folders. Files need to be re-selected if you refresh the page or switch tabs.
          </div>
          
          {/* Cloudinary Upload Progress */}
          {isUploadingToCloudinary && (
            <div className="space-y-3 bg-blue-50 border border-blue-200 rounded p-4">
              <div className="flex justify-between text-sm font-medium">
                <span>📤 Uploading images to Cloudinary...</span>
                <span>{uploadedCount}/{totalCount} completed</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
              <div className="text-xs text-blue-600 space-y-1">
                <div className="text-center">{uploadProgress.toFixed(1)}% complete</div>
                {currentFile && (
                  <div className="text-center">Currently uploading: <span className="font-mono">{currentFile}</span></div>
                )}
                <div className="text-center">Images organized by supplier folder</div>
              </div>
            </div>
          )}
          
          {/* Cloudinary Upload Error */}
          {uploadError && (
            <div className="text-xs text-red-600 text-center bg-red-50 border border-red-200 rounded p-2">
              ❌ Image upload error: {uploadError}
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                if (confirm('Are you sure you want to clear all form data? This action cannot be undone.')) {
                  form.reset();
                  setUploadedImages([]);
                  setUploadedCertifications([]);
                  clearSavedData();
                  setError(null);
                  setSuccess(false);
                }
              }}
            >
              Clear Form
            </Button>
            
            <div className="flex gap-4">
              <Button type="submit" disabled={loading || isSubmitting}>
                {loading || isSubmitting 
                  ? (isEditing ? 'Submitting Product...' : 'Adding Product...') 
                  : (isEditing ? 'Submit Product' : 'Add Product')
                }
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
});
